import type { ReactNode } from 'react';
import { formatNumber } from '../../lib/format.ts';
import { useLocale } from '../../lib/i18n.tsx';
import { colorFor } from '../palette.ts';

export interface BarItem {
  key: string | null;
  label: ReactNode;
  value: number;
  /** Small muted text after the label (for example "automatic type"). */
  note?: ReactNode;
}

/**
 * Horizontal bars for a list of categories (containers by type, source mix, loss reasons). Bars,
 * not a pie: lengths are easy to compare. The number is always printed, so colour is decoration.
 */
export function BarList({
  items,
  ariaLabel,
  unit,
}: {
  items: readonly BarItem[];
  ariaLabel: string;
  unit?: string;
}) {
  const locale = useLocale();
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <ul aria-label={ariaLabel} className="space-y-2">
      {items.map((item) => (
        <li key={item.key ?? 'none'} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 break-words">
              {item.label}
              {item.note ? (
                <span className="ml-2 text-xs text-muted-foreground">{item.note}</span>
              ) : null}
            </span>
            <span className="shrink-0 font-medium tabular-nums">
              {formatNumber(item.value, locale)}
              {unit ? (
                <span className="ml-1 text-xs font-normal text-muted-foreground">{unit}</span>
              ) : null}
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted" aria-hidden="true">
            <div
              className="h-2 rounded-full"
              style={{ width: `${(item.value / max) * 100}%`, background: colorFor(item.key) }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
